import { XMarkIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import ButtonGroup from '../forms/ButtonGroup'
import Input from '../forms/Input'
import Button, { IconButton, LinkButton } from '../shared/Button'
import store from 'stores/store'
import usePrevious from 'hooks/usePrevious'
import Tooltip from '@components/shared/Tooltip'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { SLIPPAGE_KEY } from 'utils/constants'

const slippagePresets = ['0.1', '0.5', '1', '2']

const SwapSettings = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('swap')
  const slippage = store((s) => s.swap.slippage)
  const focusRef = useRef<HTMLInputElement>(null)

  const [showCustomSlippageForm, setShowCustomSlippageForm] = useState(false)
  const [inputValue, setInputValue] = useState(slippage.toString())
  const [, setStoredSlippage] = useLocalStorage(SLIPPAGE_KEY)

  useEffect(() => {
    if (!slippagePresets.includes(slippage.toString())) {
      setShowCustomSlippageForm(true)
    }
  }, [slippage])

  const handleSetSlippage = (slippage: string, close?: boolean) => {
    setStoredSlippage(slippage.toString())
    if (close) {
      onClose()
    }
  }

  const prevShowForm = usePrevious(showCustomSlippageForm)

  useEffect(() => {
    if (focusRef?.current && !prevShowForm && showCustomSlippageForm) {
      focusRef.current.focus()
    }
  }, [prevShowForm, showCustomSlippageForm])

  return (
    <>
      <IconButton
        className="absolute right-2 top-2 text-th-fgd-3"
        onClick={onClose}
        hideBg
      >
        <XMarkIcon className="size-5" />
      </IconButton>

      <div className="mt-4">
        <div className="mb-2 flex justify-between">
          <Tooltip content={t('slippage.slippage-desc')}>
            <p className="tooltip-underline text-th-fgd-2">
              {t('max-slippage')}
            </p>
          </Tooltip>
          <LinkButton
            onClick={() => setShowCustomSlippageForm(!showCustomSlippageForm)}
          >
            {showCustomSlippageForm ? t('preset') : t('slippage.custom')}
          </LinkButton>
        </div>
        {showCustomSlippageForm ? (
          <>
            <Input
              ref={focusRef}
              type="text"
              placeholder="0.00"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              suffix="%"
            />
            <Button
              disabled={
                !inputValue ||
                isNaN(Number(inputValue)) ||
                parseFloat(inputValue) <= 0
              }
              className="mt-4"
              onClick={() => handleSetSlippage(inputValue, true)}
            >
              {t('save')}
            </Button>
          </>
        ) : (
          <ButtonGroup
            activeValue={slippage.toString()}
            className="h-10 font-mono"
            onChange={(v) => handleSetSlippage(v)}
            unit="%"
            values={slippagePresets}
          />
        )}
      </div>
    </>
  )
}

export default SwapSettings
